/**
 * Parameter Editor Component
 * Modal for editing individual function parameters
 */

import React, { useState, useEffect } from 'react';
import { ModalPortal } from '../../Modals/ModalPortal';
import '../Agent/AgentEditor.css'; // Reuse agent editor styles

export interface Parameter {
  ParameterID?: number;
  ParameterName: string;
  ParameterType: string;
  ParameterDescription: string;
  IsRequired: boolean;
  DefaultValue: string;
  DisplayOrder: number;
}

interface ParameterEditorProps {
  isOpen: boolean;
  parameter?: Parameter;
  onClose: () => void;
  onParameterSaved: (parameter: Parameter) => void;
}

const defaultParameter: Parameter = {
  ParameterName: '',
  ParameterType: 'string',
  ParameterDescription: '',
  IsRequired: false,
  DefaultValue: '',
  DisplayOrder: 0,
};

export const ParameterEditor: React.FC<ParameterEditorProps> = ({
  isOpen,
  parameter,
  onClose,
  onParameterSaved,
}) => {
  const [formData, setFormData] = useState<Parameter>(defaultParameter);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (parameter) {
        setFormData(parameter);
      } else {
        setFormData(defaultParameter);
      }
      setError(null);
    }
  }, [isOpen, parameter]);

  const handleChange = (field: keyof Parameter, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.ParameterName.trim()) {
      setError('Parameter name is required');
      return;
    }

    onParameterSaved(formData);
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        className="agent-editor-overlay"
        onClick={onClose}
      >
        <div
          className="parameter-editor-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="agent-editor-header">
            <h1 className="editor-title">
              {parameter ? ' Edit Parameter' : ' Add Parameter'}
            </h1>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </div>

          {error && (
            <div className="editor-error-banner">
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSave} className="editor-form">
            <div className="form-grid">
              <div className="form-group full">
                <label>Parameter Name *</label>
                <input
                  type="text"
                  value={formData.ParameterName}
                  onChange={(e) => handleChange('ParameterName', e.target.value)}
                  placeholder="e.g., email, userId"
                  maxLength={200}
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Parameter Type *</label>
                <select
                  value={formData.ParameterType}
                  onChange={(e) => handleChange('ParameterType', e.target.value)}
                >
                  <option value="string">String</option>
                  <option value="number">Number</option>
                  <option value="integer">Integer</option>
                  <option value="boolean">Boolean</option>
                  <option value="array">Array</option>
                  <option value="object">Object</option>
                </select>
              </div>

              <div className="form-group">
                <label>Default Value</label>
                <input
                  type="text"
                  value={formData.DefaultValue}
                  onChange={(e) => handleChange('DefaultValue', e.target.value)}
                  placeholder="e.g., user@example.com"
                  maxLength={500}
                />
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  value={formData.DisplayOrder}
                  onChange={(e) =>
                    handleChange('DisplayOrder', parseInt(e.target.value) || 0)
                  }
                  min="0"
                />
              </div>

              <div className="form-group full">
                <label>Description</label>
                <textarea
                  value={formData.ParameterDescription}
                  onChange={(e) =>
                    handleChange('ParameterDescription', e.target.value)
                  }
                  placeholder="What is this parameter used for?"
                  rows={3}
                  maxLength={1000}
                />
              </div>

              <div className="checkbox-grid">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={formData.IsRequired}
                    onChange={(e) =>
                      handleChange('IsRequired', e.target.checked)
                    }
                  />
                  <span>Required</span>
                </label>
              </div>
            </div>

            <div className="editor-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ParameterEditor;
